import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingBag } from "lucide-react";
import { fetchProduct } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const COLOR_IDS = ["qzip-white", "qzip-black"];
const DEFAULT_SIZES = ["S", "M", "L", "XL", "XXL"];

const QZip = () => {
  const [colors, setColors] = useState([]);
  const [selectedColorId, setSelectedColorId] = useState("qzip-white");
  const [selectedSize, setSelectedSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [error, setError] = useState(null);
  const { addItem, openCart } = useCart();

  useEffect(() => {
    Promise.all(COLOR_IDS.map((id) => fetchProduct(id).catch(() => null)))
      .then((results) => setColors(results.filter(Boolean)))
      .catch((e) => setError(e.message));
  }, []);

  const selected = useMemo(
    () => colors.find((c) => c.id === selectedColorId) || colors[0],
    [colors, selectedColorId],
  );

  useEffect(() => {
    setQty(1);
  }, [selectedColorId]);

  const sizes = selected?.sizes || DEFAULT_SIZES;
  const maxQty = selected?.max_per_purchase || 3;

  const handleAdd = () => {
    if (!selected) return;
    if (!selectedSize) {
      toast.error("Please choose a size first");
      return;
    }
    addItem(selected.id, qty, selectedSize);
    const colorName = selected.id === "qzip-white" ? "White" : "Black";
    toast.success(`Added ${qty} × Summit Scents Quarter-Zip (${colorName}, Size ${selectedSize}) to your cart`);
    openCart();
  };

  if (error) {
    return (
      <div className="pt-32 px-6 text-center text-red-400">
        Could not load product: {error}
      </div>
    );
  }

  if (!selected) {
    return (
      <div className="pt-32 px-6 text-center text-[var(--secondary-text)] uppercase tracking-label text-[11px]">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="qzip-page" className="pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="lg:sticky lg:top-28">
            <div className="relative bg-black aspect-square flex items-center justify-center border hairline grain-overlay overflow-hidden">
              <img
                src={selected.image}
                alt={selected.name}
                className="absolute inset-0 w-full h-full object-contain"
                data-testid="qzip-hero-image"
              />
            </div>
            <div className="flex items-center gap-4 mt-6">
              <img src="/mountain-mark.png" alt="" aria-hidden className="h-4 w-auto select-none" draggable={false} />
              <span className="text-[10px] uppercase tracking-label text-[var(--secondary-text)]">
                Hand-finished with the Summit mark. Edition 2026
              </span>
            </div>
          </div>
        </motion.div>

        {/* Details */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.2 }}
          className="lg:pt-12"
        >
          <span className="text-[10px] uppercase tracking-label gold-text">
            Summit Apparel
          </span>
          <h1 className="mt-5 font-serif text-5xl md:text-6xl leading-[1.0] tracking-tight">
            Summit Scents Quarter-Zip
          </h1>
          <p className="mt-4 text-[11px] uppercase tracking-label text-[var(--secondary-text)]">
            Performance Quarter-Zip · Unisex Fit
          </p>

          <div className="divider-gold mt-8 mb-8" />

          <p className="text-base text-[var(--secondary-text)] leading-loose max-w-lg">
            {selected.description}
          </p>

          {/* Color selector */}
          <div className="mt-12 border-t hairline pt-8">
            <p className="text-[10px] uppercase tracking-label gold-text mb-4">
              Choose colour
            </p>
            <div className="grid grid-cols-2 gap-3" data-testid="qzip-color-selector">
              {colors.map((c) => {
                const active = c.id === selectedColorId;
                const label = c.id === "qzip-white" ? "White" : "Black";
                return (
                  <button
                    key={c.id}
                    onClick={() => setSelectedColorId(c.id)}
                    data-testid={`color-option-${c.id}`}
                    className={`text-left border hairline px-5 py-4 transition-colors ${
                      active
                        ? "border-[var(--accent-gold)] bg-[#0a0a0a]"
                        : "hover:border-[var(--accent-gold)]/60"
                    }`}
                  >
                    <div className={`text-sm ${active ? "text-[var(--accent-gold)]" : "text-[var(--primary-text)]"}`}>
                      {label}
                    </div>
                    <div className="mt-1 text-[10px] uppercase tracking-label text-[var(--secondary-text)]">
                      Performance quarter-zip
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Size selector */}
          <div className="mt-10">
            <p className="text-[10px] uppercase tracking-label gold-text mb-4">
              Choose size
            </p>
            <div className="grid grid-cols-5 gap-3" data-testid="qzip-size-selector">
              {sizes.map((s) => {
                const active = s === selectedSize;
                return (
                  <button
                    key={s}
                    onClick={() => setSelectedSize(s)}
                    data-testid={`size-option-${s}`}
                    className={`border hairline py-3 text-sm transition-colors ${
                      active
                        ? "border-[var(--accent-gold)] text-[var(--accent-gold)] bg-[#0a0a0a]"
                        : "text-[var(--primary-text)] hover:border-[var(--accent-gold)]/60"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Price + qty + add */}
          <div className="mt-10">
            <div className="flex items-baseline justify-between mb-10">
              <span
                className="font-serif text-4xl gold-text"
                data-testid="qzip-price"
              >
                ${selected.price?.toFixed(2)}
              </span>
              <span className="text-[10px] uppercase tracking-label text-[var(--secondary-text)]">
                Limit {maxQty} per order
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch gap-5">
              <div className="inline-flex items-center self-start sm:self-auto">
                <button
                  className="qty-btn"
                  data-testid="qzip-decrease-btn"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  disabled={qty <= 1}
                  aria-label="Decrease quantity"
                >
                  <Minus size={14} />
                </button>
                <span
                  className="w-14 text-center text-sm tracking-label"
                  data-testid="qzip-qty"
                >
                  {qty}
                </span>
                <button
                  className="qty-btn"
                  data-testid="qzip-increase-btn"
                  onClick={() => setQty((q) => Math.min(maxQty, q + 1))}
                  disabled={qty >= maxQty}
                  aria-label="Increase quantity"
                >
                  <Plus size={14} />
                </button>
              </div>

              <button
                onClick={handleAdd}
                data-testid="qzip-add-to-cart-btn"
                className="btn-secondary inline-flex items-center justify-center gap-3 flex-1"
              >
                <ShoppingBag size={14} />
                Add to Cart — ${(selected.price * qty).toFixed(2)}
              </button>
            </div>

            <ul className="mt-10 space-y-3 text-[11px] uppercase tracking-label text-[var(--secondary-text)]">
              <li>· Athletic-Fit, Polyester/Silk Material</li>
              <li>· Embroidered gold Summit mark on chest</li>
              <li>· $6.99 flat shipping · ships within United States</li>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default QZip;
